import {
  PayWithPointsDisabledReason,
  usePayWithPoints,
} from "../../../services/hooks/loyalty/usePayWithPoints";
import type { PayWithPointsSurface } from "../../../services/hooks/loyalty/usePayWithPoints";
import { useAppSelector } from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { cn } from "../../../utils/cn";
import {
  isArabicLanguage,
  localizedNumber,
} from "../../../utils/help/localization/localization";
import {
  amountToPoints,
  formatPointsAmount,
} from "../../../utils/loyalty/payWithPoints";
import { ExpiringPointsIcon, PointsStar } from "../Icons/loyalty.icons";
import {
  PayWithPointsCircleAction,
  PayWithPointsMotifEnd,
  PayWithPointsMotifStart,
  TierMedal,
} from "./payWithPoints.icons";
import styles from "./PayWithPointsBanner.module.scss";

export const PayWithPointsBanner = ({
  variant = "menu",
  surface,
  onOpen,
}: {
  variant?: "menu" | "rewards";
  surface: PayWithPointsSurface;
  onOpen: () => void;
}) => {
  const isRewardsVariant = variant === "rewards";
  const { t } = useTranslationI18();
  const currency = useAppSelector((state) => state.config.currency);
  const isArabic = isArabicLanguage();

  const {
    isRedeemable,
    disabledReason,
    redemptionRate,
    balanceAmount,
    balancePoints,
    expiringPoints,
    hasTiers,
    currentTier,
    isSignedIn,
  } = usePayWithPoints(surface);

  const formatAmount = (amount: number) =>
    formatPointsAmount(amount, currency, isArabic);

  const rateFragment = t("pointing-system:points-conversion-rate", {
    amount: formatAmount(1),
    points: localizedNumber(amountToPoints(1, redemptionRate)),
  });

  if (!isSignedIn) {
    return (
      <div
        className={cn(
          styles.wrapper,
          isRewardsVariant && styles.wrapper_rewards
        )}
      >
        <div
          className={cn(styles.banner, styles.banner_signed_out)}
          onClick={onOpen}
          data-testid="pay-with-points-banner"
          data-state="not_signed_in"
        >
          <span className={cn(styles.motif, styles.motif_start)}>
            <PayWithPointsMotifStart />
          </span>
          <span className={cn(styles.motif, styles.motif_end)}>
            <PayWithPointsMotifEnd />
          </span>

          <span className={styles.signed_out_copy}>
            {t("pointing-system:redeem-points-as-instant-discount")}
            <span className={styles.signed_out_rate}>
              {t("pointing-system:points-conversion-rate-reversed", {
                amount: formatAmount(1),
                points: localizedNumber(amountToPoints(1, redemptionRate)),
              })}
            </span>
          </span>
        </div>
      </div>
    );
  }

  const hasCircleAction = isRedeemable && !isRewardsVariant;

  return (
    <div
      className={cn(styles.wrapper, isRewardsVariant && styles.wrapper_rewards)}
    >
      {hasTiers && currentTier && !isRewardsVariant && (
        <span className={styles.medal} data-testid="pwp-tier-medal">
          <TierMedal order={currentTier.tierOrder} height={40} />
        </span>
      )}

      <div
        className={cn(
          styles.banner,
          !isRedeemable && styles.disabled,
          hasCircleAction && styles.banner_with_action
        )}
        onClick={isRedeemable ? onOpen : undefined}
        data-testid="pay-with-points-banner"
        data-state={disabledReason?.toLowerCase() ?? "available"}
      >
        <span className={cn(styles.motif, styles.motif_start)}>
          <PayWithPointsMotifStart />
        </span>
        <span className={cn(styles.motif, styles.motif_end)}>
          <PayWithPointsMotifEnd />
        </span>

        <span className={styles.headline} data-testid="pwp-banner-headline">
          <span className={styles.headline_amount}>
            {formatAmount(balanceAmount)}
          </span>
          <span className={styles.headline_for}>
            {t("pointing-system:for")}
          </span>
          <span className={styles.headline_points}>
            {localizedNumber(balancePoints)}
          </span>
          <span className={styles.headline_points_label}>
            {t("pointing-system:points-word")}
          </span>
          <PointsStar />
        </span>

        <hr className={styles.divider} />

        <span
          className={cn(
            styles.rate_line,
            isRewardsVariant && styles.rate_stacked
          )}
        >
          {`${t(
            isRewardsVariant
              ? "pointing-system:redeem-points-as-instant-discount"
              : "pointing-system:redeem-points-as-discount"
          )} `}
          <span className={styles.rate_value}>{rateFragment}</span>
        </span>

        {!isRewardsVariant &&
          expiringPoints !== undefined &&
          expiringPoints > 0 && (
            <span className={styles.expiry} data-testid="pwp-expiry">
              <ExpiringPointsIcon />
              {`${t("pointing-system:expiring-this-month-label")} `}
              <span className={styles.expiry_value}>
                {t("pointing-system:expiring-this-month-value", {
                  points: localizedNumber(expiringPoints),
                })}
              </span>
            </span>
          )}

        {disabledReason === PayWithPointsDisabledReason.NO_CART_BALANCE && (
          <span className={styles.message}>
            {t("pointing-system:no-cart-balance")}
          </span>
        )}
        {disabledReason === PayWithPointsDisabledReason.BELOW_MINIMUM && (
          <span className={styles.message}>
            {t("pointing-system:order-to-collect-points")}
          </span>
        )}

        {hasCircleAction && (
          <span className={styles.action}>
            <PayWithPointsCircleAction />
          </span>
        )}
      </div>
    </div>
  );
};
